// @file Composable that provides methods for draggable posts
import { isPostPinned } from '@@/bits/post_state'
import { WallWishGroupByTypes } from '@@/enums'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { LogicalEdge } from '@@/pragmatic-drag-and-drop/helpers'
import type { Post } from '@@/types'
import { storeToRefs } from 'pinia'

export const useSurfacePostDraggableHelper = (): {
  canDropPost: typeof canDropPost
  getEdgeForDropTargetPost: typeof getEdgeForDropTargetPost
} => {
  const { isSectionedAndNonmanualSorted, wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const { allSortedPostsByGroup } = storeToRefs(useSurfacePostsStore())

  function isDraggedToTopOfGroup(targetPost: Post, targetGroupId: number): boolean {
    const posts = allSortedPostsByGroup.value[targetGroupId]
    if (posts === undefined) return true
    if (posts.length === 0) return true
    return posts[0].cid === targetPost.cid
  }
  function isDraggedToBottomOfGroup(targetPost: Post, targetGroupId: number): boolean {
    const posts = allSortedPostsByGroup.value[targetGroupId]
    if (posts === undefined) return true
    if (posts.length === 0) return true
    return posts[posts.length - 1].cid === targetPost.cid
  }

  function canDropPost(draggedPost: Post, targetPost: Post): boolean {
    // if we aren't sorting manually, don't allow D&D operations
    if (isSectionedAndNonmanualSorted.value) {
      return false
    }
    const isDraggedPostPinned = isPostPinned(draggedPost)
    const isTargetPostPinned = isPostPinned(targetPost)

    if (wishGroupBy.value === WallWishGroupByTypes.None) {
      // For none group, pinned posts can only be dragged to pinned posts. and same for unpinned posts
      if (isDraggedPostPinned && !isTargetPostPinned) {
        return false
      }
      if (!isDraggedPostPinned && isTargetPostPinned) {
        return false
      }
      return true
    }

    // related element is a post -> we can drag there conditionally
    const targetGroup = surfaceSectionsStore.sectionEntities[targetPost.wall_section_id ?? -1]
    if (targetGroup?.id == null) {
      return false
    }
    // if dragged is pinned and incoming group only has unpinned posts and we are dragging to the top of the group, D&D is OK
    if (
      isDraggedPostPinned &&
      !surfaceSectionsStore.hasPinnedPosts(targetGroup.id) &&
      isDraggedToTopOfGroup(targetPost, targetGroup.id)
    ) {
      return true
    }
    // conversely, if post is unpinned and incoming group is entirely pinned, and we are dragging to the bottom, D&D is OK
    if (
      !isDraggedPostPinned &&
      !surfaceSectionsStore.hasUnpinnedPosts(targetGroup.id) &&
      isDraggedToBottomOfGroup(targetPost, targetGroup.id)
    ) {
      return true
    }
    // otherwise, pinned posts can only be dragged to pinned posts. and same for unpinned posts
    if (isDraggedPostPinned && !isTargetPostPinned) {
      return false
    }
    if (!isDraggedPostPinned && isTargetPostPinned) {
      return false
    }
    return true
  }

  function getEdgeForDropTargetPost(
    dropTargetPost: Post,
    sourcePost: Post,
    axis: 'vertical' | 'horizontal',
  ): LogicalEdge | undefined {
    const isDropTargetPostPinned = isPostPinned(dropTargetPost)
    const isDraggedPostPinned = isPostPinned(sourcePost)
    // If post being dragged is bit pinned and the drop target post is, we can only drop
    // the dragged post below of the drop target post
    if (!isDraggedPostPinned && isDropTargetPostPinned) return axis === 'vertical' ? 'bottom' : 'end'
    // If post being dragged is pinned and the drop target post is not, we can only drop
    // the dragged post on top of the drop target post
    if (isDraggedPostPinned && !isDropTargetPostPinned) return axis === 'vertical' ? 'top' : 'start'
    return undefined
  }

  return {
    canDropPost,
    getEdgeForDropTargetPost,
  }
}
